import React, { useState } from 'react';
import { css } from '@emotion/react';
import { theme, Flex, Flow, Text, Button, Toggle } from '@xometry/xometry_loft';
import PunchoutHidden from '../../PunchoutHidden';
import {
  initiatePasswordReset,
  initiateResendEmailVerification,
  notHandledButtonClick,
  addMfaMethod,
  deleteMfaMethod,
} from './helpers';

interface Props {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  email_verified: boolean;
  phoneNumber: string;
  userId: string;
  staffMode: boolean;
  displayNotification: any;
  mfaMethod: string | null;
}

type ButtonActionType = 'sendEmailVerification' | 'sendPasswordReset';

const emailContainer = css`
  padding-bottom: 16px;
  border-bottom: 1px solid ${theme.colors.darkBlues[25]};
`;

const securityPanel = css`
  display: inline-block;
  font-weight: 600;
  margin-bottom: 12px;
  color: #253842;
  font-size: 16px;
  line-height: 24px;
`;

const NotificationExtraOptions = {
  hasLearnMoreLink: false,
  learnMorePath: '',
  hasIcon: true,
  hideClose: true,
};

const passwordStyle = css`
  margin-top: 16px;
`;

const UserDisplay = ({
  firstName,
  lastName,
  companyName,
  email,
  email_verified,
  phoneNumber,
  userId,
  staffMode,
  displayNotification,
  mfaMethod,
}: Props) => {
  const [enableMfa, setEnableMfa] = useState<boolean>(!!mfaMethod);
  const [isHandlingClick, setIsHandlingClick] =
    useState<ButtonActionType | null>(null);

  const handleFetch = async (buttonClicked: ButtonActionType) => {
    if (buttonClicked === 'sendEmailVerification') {
      return await initiateResendEmailVerification(userId, email);
    } else if (buttonClicked === 'sendPasswordReset') {
      return await initiatePasswordReset(staffMode, userId, email);
    } else {
      return notHandledButtonClick(buttonClicked);
    }
  };

  const handleClick = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setIsHandlingClick(e.currentTarget.name as ButtonActionType);
    const { notificationType, notificationMessage } = await handleFetch(
      e.currentTarget.name as ButtonActionType
    );
    displayNotification(
      notificationType,
      notificationMessage,
      NotificationExtraOptions
    );
    setIsHandlingClick(null);
  };

  const handleEmailMfaToggle = async (isChecked) => {
    setEnableMfa((prevState) => !prevState);
    if (isChecked) {
      const mfa_method = 'email';
      const {
        notificationType,
        notificationMessage,
        error,
        resendVerificationEmail,
      } = await addMfaMethod(userId, mfa_method);

      if (error) {
        if (resendVerificationEmail) {
          NotificationExtraOptions.hasLearnMoreLink = true;
          NotificationExtraOptions.learnMorePath = '/'; // TODO: change this path to the path of verify email first learn more link
          await initiateResendEmailVerification(userId, email);
        }
        setEnableMfa(false);
      } else {
        NotificationExtraOptions.hasLearnMoreLink = true;
        NotificationExtraOptions.learnMorePath =
          'https://community.xometry.com/kb/articles/702-how-to-create-a-xometry-customer-account';
      }

      displayNotification(
        notificationType,
        notificationMessage,
        NotificationExtraOptions
      );
    } else {
      const { notificationType, notificationMessage, error } =
        await deleteMfaMethod(userId);

      if (error) {
        setEnableMfa(true);
      } else {
        NotificationExtraOptions.hasLearnMoreLink = true;
        NotificationExtraOptions.learnMorePath =
          'https://community.xometry.com/kb/articles/702-how-to-create-a-xometry-customer-account';
      }

      displayNotification(
        notificationType,
        notificationMessage,
        NotificationExtraOptions
      );
    }
  };

  return (
    <Flex flexDirection="column" gap={theme.space[3]}>
      <div>
        <Text kind="label">Name</Text>
        <Text className="pii-encrypt">
          {firstName} {lastName}
        </Text>
      </div>
      <div>
        <Text kind="label">Company Name</Text>
        <Text>{companyName}</Text>
      </div>
      <div>
        <Text kind="label">Phone Number</Text>
        <Text>{phoneNumber}</Text>
      </div>
      <div css={emailContainer}>
        <Text kind="label">Email</Text>
        <Flex
          flexWrap="wrap"
          className="pii-encrypt"
          gap={theme.space[3]}
          alignItems="center"
        >
          <Text>{email}</Text>
          {email_verified && (
            <Flex
              color={theme.colors.greens[125]}
              alignItems="center"
              gap={theme.space[1]}
            >
              <Text kind="body-title" color={theme.colors.greens[125]}>
                Verified
              </Text>
              <i title="Email Verified" className="far fa-check-circle" />
            </Flex>
          )}
          {!email_verified && (
            <>
              {(!isHandlingClick ||
                isHandlingClick !== 'sendEmailVerification') && (
                <Button
                  kind="text-red"
                  title="Resend Email Verification"
                  data-testid="unverified-email-button"
                  name="sendEmailVerification"
                  onClick={(e) => handleClick(e)}
                >
                  <Text kind="body-title" color={theme.colors.reds[125]}>
                    Unverified{' '}
                  </Text>
                  <i className="far fa-times-circle" />
                </Button>
              )}
              {isHandlingClick === 'sendEmailVerification' && (
                <Flow
                  data-testid="handling-resend-email-verification"
                  marginLeft={theme.space[3]}
                  scale="panel"
                  kind="primary"
                />
              )}
            </>
          )}
        </Flex>
      </div>
      <div>
        <PunchoutHidden>
          <Flex flexDirection="column">
            <div css={securityPanel}>Security</div>
            <Flex flexDirection="column" gap={theme.space[3]}>
              <Text kind="label" fontWeight={theme.fontWeights.semibold}>
                Multi-Factor Authentication
              </Text>
              <Toggle
                name="email"
                handleChange={(check: boolean) => handleEmailMfaToggle(check)}
                checked={enableMfa}
                label={
                  <Text
                    fontSize={theme.fontSizes[2]}
                    kind="body-sm"
                    style={{ whiteSpace: 'normal' }}
                  >
                    Email MFA
                  </Text>
                }
              />
            </Flex>
          </Flex>
          <div css={passwordStyle}>
            <Text kind="label" fontWeight={theme.fontWeights.semibold}>
              Password
            </Text>
            {(!isHandlingClick || isHandlingClick !== 'sendPasswordReset') && (
              <Button
                width="fit-content"
                scale="small"
                kind="text-blue"
                name="sendPasswordReset"
                onClick={(e) => handleClick(e)}
              >
                <Flex>{staffMode ? 'Reset Password' : 'Change Password'}</Flex>
              </Button>
            )}
          </div>

          {isHandlingClick === 'sendPasswordReset' && (
            <Button width="fit-content" scale="small" kind="text-blue">
              <Flow
                data-testid="handling-password-reset"
                marginLeft={theme.space[3]}
                scale="panel"
                kind="primary"
              />
            </Button>
          )}
        </PunchoutHidden>
      </div>
    </Flex>
  );
};

export default UserDisplay;
